import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
  useCallback
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastQueue } from '@adobe/react-spectrum';
import { getCookie } from 'utils/helpers';
import { updateMoment } from '../../RsdForms/api';
import { handle401Error, handle403Error } from 'utils/authErrorHandler';
import { useDispatch, useSelector } from 'react-redux';
import { pendingApprovalsActions } from '../../../store/pendingApprovalsSlice';
import styles from './MilestoneMatrixFromAPI.module.scss';
import { EditMilestoneModal } from '../../../../components/Roadmap/edit-milestone';
import type { SlcApprovalSubjectContext } from 'utils/slcApprovalSubject';
import MilestoneHoverPopup from '../../MilestoneHoverPopup';
import { stripHtml } from '../CommentPanel/utils';
// Enhanced imports - these provide full Excel compatibility
import {
  ENHANCED_MILESTONE_STATUSES as MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS as STATUS_MENUS,
  getEnhancedEditFields as getVisibleFields,
  getMilestoneApprovalRequired,
  validateEnhancedMilestoneData as validateMilestoneData,
  formatEnhancedCellDisplay as formatMilestoneDisplayText,
  getEnhancedCellStyling as getCellStyling,
  formatEnhancedPopupText as formatMilestonePopupText,
  getEnhancedMilestoneIcon,
  MILESTONE_ICONS,
  getExcelHoverData,
  getMilestoneExcludedReviewTypes
} from './milestoneConfigEnhanced';
import { MilestoneIcons } from '../../Icons/MilestoneIcons';
import { sortMilestones } from '../../../pages/MilestonesGrid/utils/milestoneSortUtils';
import {
  updateMilestone,
  MilestoneUpdateRequest,
  MilestoneUpdateResponse
} from '../configs/mockData';
import { selectMilestoneAttributesData } from '../../../store/globalCacheSlice';
import type { AlertIssue } from '../../AlertsAndNotification/store/types';
import { useActiveAlertIssues } from '../../AlertsAndNotification/hooks/useActiveAlertIssues';
import { shouldHighlightMilestoneAlertCell } from '../../AlertsAndNotification/utils/milestoneDeclarativeHighlight';
import {
  resolveAlertScrollWinner,
  PhaseOrderEntry
} from '../../AlertsAndNotification/utils/alertScrollPriority';
import type { MilestoneAttributesData } from '../../../services/milestoneAttributesService';
import {
  toUtcIsoFromLocalParts,
  formatLocalTimeFromUtc,
  getTimezoneAbbreviation,
  normalizeTimezoneInString,
  utcLabelTimeToLocal24,
  convertMilestoneTimestampToLocal
} from '../../../../utils/time';
import { useRBAC } from '../context/RBACContext';
import { areArraysEqual } from '../../../../utils/comparison';
import {
  inferTypeOfReviewFromStatus,
  getCellWidthClass,
  getDescriptiveFieldLabel,
  getMilestoneDateTextStyle,
  isMilestonePastDue,
  pickMilestoneFieldsFromPatchResponse,
  stripPatchResponseEnvelopeFields,
  findEmailSentInPatchResponse
} from './milestoneDisplayUtils';
import { mapMilestoneDateFields } from './milestoneDateFieldMapper';
import { generateMilestoneEmailTemplate } from './milestoneCopyUtils';
import {
  MilestoneReviewTypeInlinePicker,
  type InlineReviewChoice
} from './MilestoneReviewTypeInlinePicker';
import {
  MILESTONE_REVIEW_TYPE,
  MILESTONE_EMAIL_SENT_SUCCESS_MESSAGE,
  MILESTONE_EMAIL_NOT_SENT_ERROR_MESSAGE,
  isEolMoment,
  isPriceChangeStyleOfferMoment
} from '../../../../utils/constants';
import {
  isAsyncSentSendForApprovalAttempt,
  isApprovedOrReApprovedStatus,
  resolveOptimisticEmailSent,
  getNewApprovalCycleState,
  isInactiveApprovalReviewType
} from '../../../../utils/approvalFlow';
import { fetchMilestoneApprovalStatus } from '../../../services/momentsApi';
// Same icon Review & Approval's own Feedback column uses (FeedbackCell.tsx)
// - reused as-is, not a new/different icon for this entry point.
import S2CommentTextIcon from 'app/assets/icons/S2_Icon_CommentText_20_N.svg';

interface MilestoneAttributes {
  async: boolean;
  audited: boolean;
  approvalRequired: boolean;
}

interface Milestone {
  id?: string; // ← ADD: Milestone ID from API
  name: string;
  required: boolean;
  typeOfReview?: string;
  reviewType?: string; // Alternative property name used in mockData
  status?: string;
  date?: string;
  meetingScheduledOn?: string;
  meetingScheduledAt?: string;
  displayScheduleDateTime?: string; // NEW: UTC/GMT timestamp combining date + time for accurate cross-timezone display
  meetingLink?: string;
  secondaryDate?: string;
  documentLink?: string;
  recordingLink?: string;
  notesLink?: string;
  approver?: string;
  approverUid?: string;
  // Historical/enhanced fields
  originalApprovedOn?: string;
  reApprovedOn?: string;
  approvedBy?: string;
  reApprovedBy?: string;
  /** Who rejected it, when status is Not Approved (incl. async variant). */
  notApprovedBy?: string;
  // Milestone attributes from API
  milestoneAttributes?: MilestoneAttributes;
  /** Optional API fields for edit-modal autofill logic */
  approvedOn?: string;
  completedOn?: string;
  /** `false` = API default date (green); `true` = user-edited (default text color). */
  isEdited?: boolean;
  /** Persisted Send-for-Approval data (round-tripped from the API). */
  sendForApproval?: boolean;
  cc?: string;
  ccUid?: string;
  subject?: string;
  noteToApprover?: string;
  /** Whether the Send-for-Approval email has actually gone out (round-tripped from the API). */
  emailSent?: boolean;
  /** Drives the Feedback icon's unread-dot badge (see .feedbackIconButton/
   * .feedbackBadge below) - same field/meaning as ReviewApproval's own
   * `ApprovalRequestRow.hasUnreadFeedback`, now also present on this API's
   * own milestone objects. */
  hasUnreadFeedback?: boolean;
}

/** PDF link annotations require a real, preferably absolute, href. */
const normalizeMilestoneLink = (value: string): string => {
  const url = value.trim();
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${window.location.origin}${url}`;
  if (url.startsWith('www.') || url.includes('.')) return `https://${url}`;
  return url;
};

/**
 * Type-of-review for display and status rules. Prefer `reviewType` (API / PATCH) over
 * legacy `typeOfReview` so matrix cells match `MilestoneHoverPopup` when the two diverge.
 */
const effectiveReviewType = (
  milestone: Milestone | null | undefined
): string | undefined => milestone?.reviewType || milestone?.typeOfReview;

/** Aligns with alert fieldRef milestone segment (spaces → underscores). */
const milestoneNameKeyAttr = (name: string): string =>
  name.replace(/\s+/g, '_');

/** Phase prefix in fieldRef for phased milestones (e.g. Public_Beta.milestone....). */
const milestonePhaseKeyAttr = (phaseName: string): string =>
  phaseName.replace(/\s+/g, '_');

const isMvpMilestoneName = (name?: string): boolean =>
  name?.trim().toUpperCase() === 'MVP';

/** `data-testid` on header cells: `milestone-header-cell-${index}` */
const MILESTONE_HEADER_CELL_TEST_ID_RE = /^milestone-header-cell-(\d+)$/;

interface MilestoneMatrixProps {
  milestones: Milestone[];
  onMilestoneUpdate?: (
    milestoneIndex: number,
    updatedMilestone: Milestone
  ) => void;
  onDataRefresh?: (patchResponse?: unknown) => void; // Add callback to trigger data refresh
  onNotificationRefresh?: (patchResponse?: unknown) => void; // Callback to refresh notifications after successful PATCH
  isEditable?: boolean;
  issueId: string; // Required for API calls
  milestoneId?: string; // Optional, required only for milestone updates
  isFullScreen?: boolean; // Optional, controls chevron visibility in full screen mode
  tier?: string; // Optional tier information (L1, L2, L3) for conditional default approver
  isCommentPanelOpen?: boolean; // Optional, adjusts table width when comment panel is open
  // Sort functionality props
  sortBy?: 'date' | 'name' | 'none';
  onSortChange?: (sortBy: 'date' | 'name' | 'none') => void;
  parentMomentId?: string; // Optional parent moment ID for inherited milestones
  momentName?: string; // For audit logging
  momentType?: string; // For audit logging - RSD Type
  phaseName?: string; // Optional phase name for audit logging
  phaseId?: string; // Optional phase ID for audit logging
  /** Current overview mvpStatus content - preserved when syncing MVP milestone status to overview */
  currentOverviewMvpStatusContent?: string;
  /** PCO flag on legacy "Offer, Price Change, or Program" moments — gates the MVP-status Overview sync below (see `pco` prop docs on {@link isPriceChangeStyleOfferMoment}). */
  pco?: boolean;
  /**
   * Alerts/highlights apply only to this moment's milestone table — not inherited parent rows.
   * @default 'release'
   */
  alertHighlightScope?: 'release' | 'inherited';
  /** Increments when parent triggers a data refresh; clears the optimistic-update guard so fresh props sync in without a remount. */
  refreshToken?: number;
  /** Product group codes for the moment (e.g. ["CPG", "DPG"]). Forwarded to EditMilestoneModal for default-approver lookup. */
  productGroups?: string[];
  /**
   * Whether the parent slideout is currently open. Used only to scroll the winning alerted
   * milestone into view exactly once per slideout-open (mirrors FeaturesContainer's
   * isOpen-transition-reset pattern) — not required for the existing declarative highlight logic
   * above, which is driven purely by activeAlertIssues.
   */
  isOpen?: boolean;
  /**
   * Phase order ([] for non-phased moments) — passed straight through to
   * {@link resolveAlertScrollWinner} so this matrix instance only scrolls when Milestones actually
   * win the cross-section priority contest AND (for phased moments) the winner belongs to THIS
   * phase instance (matched via `phaseId` below).
   */
  phases?: PhaseOrderEntry[];
  /**
   * Moment-level context (name, type, dates) from the phase/moment `generalInfo`,
   * forwarded to EditMilestoneModal to build the Send-for-Approval subject line.
   */
  sendForApprovalContext?: SlcApprovalSubjectContext;
  /**
   * Opens the Review & Approval Feedback slide-out for a milestone (see
   * SlideOutPanel/index.tsx's own `openMilestoneFeedback`) - optional and
   * omitted for the inherited-milestones matrix instance (those milestones
   * belong to a different, parent moment - see that call site's own
   * comment). The Feedback icon (`.feedbackIconButton`) only renders when
   * this is provided, so omitting it is how that matrix instance opts out,
   * not a separate flag.
   *
   * Takes an already-resolved `{issueId, milestoneId, milestoneName}`, not
   * a raw `Milestone` - the click handler below resolves `milestoneId`
   * itself (`(milestone as any).id || milestoneId`, the exact same fallback
   * `fetchMilestoneApprovalStatus`'s own call site above already uses,
   * since an individual milestone entry doesn't reliably carry its own
   * `id`), so the parent doesn't need to (and, critically, can't correctly)
   * re-derive it from data it doesn't have at that level.
   */
  onOpenFeedback?: (feedbackContext: {
    issueId: string;
    milestoneId: string;
    milestoneName: string;
  }) => void;
}

// Helper function to get milestone attributes from API response
const getMilestoneAttributesFromData = (
  milestoneName: string,
  attributesData: MilestoneAttributesData | null
): MilestoneAttributes | undefined => {
  if (!attributesData?.milestone_attributes) {
    return undefined;
  }

  // Try exact match first
  let attributes = attributesData.milestone_attributes[milestoneName];

  // If no exact match, try some common variations
  if (!attributes) {
    // Try without spaces
    const noSpaceName = milestoneName.replace(/\s+/g, '');
    attributes = attributesData.milestone_attributes[noSpaceName];
  }

  // Try with spaces removed and check common mappings
  if (!attributes) {
    const mappings: { [key: string]: string } = {
      'Creative Arch': 'CreativeArch',
      'Media Brainstorm': 'MediaPosition', // Based on the attributes file
      'Mktg Pkg': 'MktgPkg',
      // Add more mappings based on the console output and attributes file
      'CA/APS': 'CA/APS',
      MVP: 'MVP',
      GTMS: 'GTMS',
      BRND: 'BRND',
      MONTZN: 'MONTZN',
      GTMP: 'GTMP',
      E2E1: 'E2E1',
      E2E2: 'E2E2',
      RR1: 'RR1',
      RR2: 'RR2',
      ReleaseMSG: 'ReleaseMSG'
    };

    const mappedName = mappings[milestoneName];
    if (mappedName) {
      attributes = attributesData.milestone_attributes[mappedName];
    }
  }

  if (!attributes) {
    return undefined;
  }

  return {
    async: attributes.async || false,
    audited: attributes.audited || false,
    approvalRequired: attributes.approvalRequired || false
  };
};

// Helper function to enhance milestones with attributes
const enhanceMilestonesWithAttributes = (
  milestones: Milestone[],
  attributesData: MilestoneAttributesData | null
): Milestone[] => {
  return milestones.map(milestone => ({
    ...milestone,
    milestoneAttributes: getMilestoneAttributesFromData(
      milestone.name,
      attributesData
    )
  }));
};

/**
 * `localMilestones` keeps API/parent order; the table renders sorted `enhancedMilestones`.
 * The pencil passes the sorted **display** index — map to the correct `localMilestones` row
 * so optimistic PATCH updates (Meeting/Async, etc.) hit the intended milestone.
 */
const resolveLocalMilestoneRowIndex = (
  rows: Milestone[],
  milestone: Milestone,
  sortedViewIndexFallback: number
): number => {
  const id = (milestone as any)?.id;
  if (id != null && String(id).length > 0) {
    const byId = rows.findIndex(m => String((m as any).id) === String(id));
    if (byId >= 0) return byId;
  }
  if (milestone?.name) {
    const byName = rows.findIndex(m => m.name === milestone.name);
    if (byName >= 0) return byName;
  }
  if (sortedViewIndexFallback >= 0 && sortedViewIndexFallback < rows.length) {
    return sortedViewIndexFallback;
  }
  return sortedViewIndexFallback;
};

// Persists horizontal scroll position across key-forced remounts (milestone saves)
let savedMilestoneScrollLeft = 0;

const MilestoneMatrixFromAPI: React.FC<MilestoneMatrixProps> = ({
  milestones = [],
  onMilestoneUpdate,
  onDataRefresh,
  onNotificationRefresh,
  isEditable = true,
  issueId,
  milestoneId,
  isFullScreen = false,
  tier,
  isCommentPanelOpen = false,
  sortBy = 'none',
  onSortChange,
  parentMomentId,
  momentName,
  momentType,
  phaseName,
  phaseId,
  currentOverviewMvpStatusContent,
  pco,
  alertHighlightScope = 'release',
  refreshToken,
  productGroups = [],
  isOpen,
  phases = [],
  sendForApprovalContext,
  onOpenFeedback
}) => {
  // React Router hooks for navigation
  const navigate = useNavigate();
  const location = useLocation();

  // RBAC: Get permissions from context
  const permissions = useRBAC();
  const canEditMilestones = permissions?.canEditMilestones ?? false;

  // State declarations first
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [reviewTypePickerColumnIndex, setReviewTypePickerColumnIndex] =
    useState<number | null>(null);
  const inlinePickerAnchorRef = useRef<HTMLElement | null>(null);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(
    null
  );
  const [selectedMilestoneIndex, setSelectedMilestoneIndex] = useState(-1);
  const [localUpdateTrigger, setLocalUpdateTrigger] = useState(0);
  const [localMilestones, setLocalMilestones] =
    useState<Milestone[]>(milestones);

  /** Set right before `onDataRefresh()` fires from the approval-status check below —
   * the open Edit modal was rendered from the milestone snapshot at click-time, which
   * `fetchMilestoneApprovalStatus` just told us is stale (approver already acted
   * outside this session). `onDataRefresh` is fire-and-forget (no promise), so this
   * flags the next `enhancedMilestones` recompute it produces as the one to pull into
   * `selectedMilestone` — see the effect below. Scoped to only this one known-stale
   * scenario (not every `enhancedMilestones` change) so it never clobbers a field the
   * user is actively editing in an already-open, already-fresh modal. */
  const pendingApprovalStatusRefreshRef = useRef<number | null>(null);
  const approvalEditRevisionRef = useRef(0);
  const invalidateApprovalStatusCheck = useCallback(() => {
    approvalEditRevisionRef.current += 1;
    pendingApprovalStatusRefreshRef.current = null;
  }, []);

  useEffect(
    () => invalidateApprovalStatusCheck,
    [invalidateApprovalStatusCheck, issueId, isOpen]
  );

  // Get milestone attributes data from global Redux cache
  const milestoneAttributesData = useSelector(selectMilestoneAttributesData);
  const dispatch = useDispatch();

  const activeAlertIssues = useActiveAlertIssues(issueId);

  const getDeclarativeMilestoneHighlightIssue = useCallback(
    (
      milestone: Milestone,
      cellSurface: 'header' | 'date'
    ): AlertIssue | null => {
      if (
        alertHighlightScope === 'inherited' ||
        activeAlertIssues.length === 0
      ) {
        return null;
      }
      for (const issue of activeAlertIssues) {
        if (
          shouldHighlightMilestoneAlertCell(issue, {
            milestoneName: milestone.name,
            phaseName,
            cellSurface
          })
        ) {
          return issue;
        }
      }
      return null;
    },
    [alertHighlightScope, activeAlertIssues, phaseName]
  );

  /** Root of this matrix instance — used to close inline review picker on outside press (other phases/cells) */
  const matrixWrapperRef = useRef<HTMLDivElement>(null);

  // Scroll functionality state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);

  // RAF ref for debouncing scroll events
  const rafRef = useRef<number | null>(null);

  // Tracks whether we've already restored scroll position this mount cycle
  const hasRestoredScrollRef = useRef(false);

  // Save scroll position on unmount so it survives key-forced remounts.
  // Must be useLayoutEffect (not useEffect): React nulls ref.current before
  // passive-effect (useEffect) cleanups run, so the ref would be null too late.
  // useLayoutEffect cleanup runs during the commit phase while refs are still live.
  useLayoutEffect(() => {
    return () => {
      if (scrollContainerRef.current) {
        savedMilestoneScrollLeft = scrollContainerRef.current.scrollLeft;
      }
    };
  }, []);

  // Track successful updates to prevent prop sync from overwriting optimistic updates
  const successfulUpdateRef = useRef<{
    index: number;
    timestamp: number;
  } | null>(null);

  // True pre-edit snapshot, captured once in handleEditMilestone before any inline
  // review-type-picker pre-mutation of `selectedMilestone` (see
  // handleInlineReviewTypeSelect, which overwrites `selectedMilestone.status`/
  // `.reviewType` with the picked value BEFORE the edit modal opens for a brand-new
  // milestone that has no reviewType yet — e.g. right after delete + "Add Milestone").
  // Without this, the MVP status/reviewType diff below compares the saved data
  // against that already-mutated selectedMilestone instead of the true prior value,
  // so the diff is always "unchanged" and the Overview PATCH never fires.
  const originalMilestoneSnapshotRef = useRef<Milestone | null>(null);

  // Sort functionality is now handled by parent component through props

  // Sync props → local state. After moment refetch (`refreshToken`), always apply fresh API data.
  useEffect(() => {
    if (milestones.length === 0) {
      return;
    }

    if (refreshToken) {
      successfulUpdateRef.current = null;
      setLocalMilestones(milestones);
      return;
    }

    if (successfulUpdateRef.current) {
      const timeSinceUpdate =
        Date.now() - successfulUpdateRef.current.timestamp;
      if (timeSinceUpdate < 2000) {
        return;
      }
      successfulUpdateRef.current = null;
    }

    if (!areArraysEqual(milestones, localMilestones)) {
      setLocalMilestones(milestones);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestones, refreshToken]);

  // Force re-render whenever local milestones change
  useEffect(() => {
    // Clear hover popup when milestones change to prevent stale data
    setHoverPopup(prev => ({
      ...prev,
      isVisible: false,
      milestone: null
    }));
  }, [localMilestones]);

  // Sort milestones based on current sort option
  const sortedMilestones = useMemo(() => {
    return sortMilestones(localMilestones, sortBy);
  }, [localMilestones, sortBy]);

  // Enhance milestones with attributes data - ALWAYS use local state for instant updates
  const enhancedMilestones = useMemo(() => {
    const enhanced = enhanceMilestonesWithAttributes(
      sortedMilestones,
      milestoneAttributesData
    );
    return enhanced;
  }, [sortedMilestones, milestoneAttributesData, localUpdateTrigger]);

  // Pulls the just-refreshed row into the open Edit modal once `onDataRefresh()`
  // (triggered by the approval-status check in `handleEditMilestone`) finishes
  // updating `enhancedMilestones` — otherwise the modal keeps showing the status
  // snapshot from click-time (e.g. "Sent") even though the backend already
  // resolved it (e.g. "Approved"), until the user closes and reopens it.
  useEffect(() => {
    if (
      pendingApprovalStatusRefreshRef.current === null ||
      pendingApprovalStatusRefreshRef.current !==
        approvalEditRevisionRef.current ||
      !isEditModalOpen ||
      !selectedMilestone
    ) {
      return;
    }
    pendingApprovalStatusRefreshRef.current = null;
    const rowIndex = resolveLocalMilestoneRowIndex(
      enhancedMilestones,
      selectedMilestone,
      selectedMilestoneIndex
    );
    const latest = enhancedMilestones[rowIndex];
    if (latest) {
      setSelectedMilestone(latest);
    }
  }, [
    enhancedMilestones,
    isEditModalOpen,
    selectedMilestone,
    selectedMilestoneIndex
  ]);

  // Cross-section scroll priority winner (date/general-info, milestones, features — whichever is
  // visually topmost; see alertScrollPriority.ts). Milestones never scrolled anywhere before this
  // change — gate so it only fires when Milestones actually win the priority contest AND (for a
  // phased instance) the winner belongs to THIS phase. Inherited parent milestone tables never
  // scroll (mirrors the `alertHighlightScope === 'inherited'` guard used for declarative highlight
  // above).
  const scrollPriorityWinner = useMemo(
    () => resolveAlertScrollWinner(activeAlertIssues, phases),
    [activeAlertIssues, phases]
  );
  const milestonesWonScrollPriority =
    alertHighlightScope !== 'inherited' &&
    scrollPriorityWinner?.mapping.section === 'milestones' &&
    scrollPriorityWinner?.phaseId === phaseId;

  // Kept fresh every render (no dep array) so the scroll-attempt effect below — which
  // intentionally only re-runs on `isOpen` transitions, see comment there — can read the
  // up-to-date winner during its 800ms/2000ms retry window without re-arming on every alert
  // change while the slideout stays open.
  const milestonesWonScrollPriorityRef = useRef(milestonesWonScrollPriority);
  useEffect(() => {
    milestonesWonScrollPriorityRef.current = milestonesWonScrollPriority;
  });

  /**
   * Scroll this milestone matrix into view — at most once per slideout-open (or per fresh data
   * load for this issueId), same "shouldScroll only on first application" / isOpen-transition-
   * reset semantics as `useFieldHighlight.ts` and `FeaturesContainer.tsx`'s alerted-feature-row
   * scroll effect. Scrolls the whole matrix wrapper (row-level granularity, matching the Features
   * row scroll) rather than a single date cell, since milestones are columns, not rows.
   *
   * Deliberately keyed on `[isOpen]` only (not `milestonesWonScrollPriority`): the scroll decision
   * is made once, within the 800ms/2000ms window right after the slideout opens, and is never
   * re-armed by a later change in which section wins the priority contest (e.g. the user resolves
   * a higher-priority alert while the panel stays open, making Milestones newly "topmost" — that
   * should NOT trigger a fresh scroll; scrolling is only expected on open/refresh).
   */
  const hasScrolledToAlertedMilestoneRef = useRef(false);
  const previousIsOpenForMilestoneScrollRef = useRef(false);

  useEffect(() => {
    if (!isOpen && previousIsOpenForMilestoneScrollRef.current) {
      hasScrolledToAlertedMilestoneRef.current = false;
    }
    previousIsOpenForMilestoneScrollRef.current = !!isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (hasScrolledToAlertedMilestoneRef.current) return;

    const attemptScroll = () => {
      if (hasScrolledToAlertedMilestoneRef.current) return;
      if (!milestonesWonScrollPriorityRef.current) return;
      const target = matrixWrapperRef.current;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        hasScrolledToAlertedMilestoneRef.current = true;
      }
    };

    // Two-attempt retry (same 800ms/2000ms cadence as useFieldHighlight.ts / FeaturesContainer.tsx).
    const timer1 = setTimeout(attemptScroll, 800);
    const timer2 = setTimeout(() => {
      attemptScroll();
      // Close the open/refresh scroll window regardless of outcome — no further attempts until
      // the next isOpen close→open transition, even if the winner changes afterward.
      hasScrolledToAlertedMilestoneRef.current = true;
    }, 2000);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [isOpen]);

  // Local short timezone string for display (e.g., PST/PDT/IST)
  // Use the shared utility function for consistent timezone abbreviation
  const tzShort = useMemo(() => {
    return getTimezoneAbbreviation();
  }, []);

  // Debug: Log enhanced milestones to see attributes
  //  // enhancedMilestones.forEach(m => {
  //  // });

  // Hover popup state
  const [hoverPopup, setHoverPopup] = useState<{
    isVisible: boolean;
    milestone: Milestone | null;
    position: { x: number; y: number };
    cellPosition?: { left: number; top: number; width: number; height: number };
    arrowDirection: 'up' | 'down';
    horizontalAlign?: 'left' | 'center' | 'right';
  }>({
    isVisible: false,
    milestone: null,
    position: { x: 0, y: 0 },
    arrowDirection: 'down',
    horizontalAlign: 'left'
  });

  const dismissReviewTypePicker = useCallback(() => {
    inlinePickerAnchorRef.current = null;
    setReviewTypePickerColumnIndex(null);
    setSelectedMilestone(null);
    setSelectedMilestoneIndex(-1);
  }, []);

  /**
   * Close the inline review-type menu when the user presses anywhere except on the menu
   * itself or on the pencil for the same column in this matrix (so same-cell toggle still runs on click).
   * Window + capture ensures other phases’ milestone cells and other matrices dismiss this picker.
   */
  useLayoutEffect(() => {
    if (reviewTypePickerColumnIndex === null) return;

    const PICKER_SELECTOR =
      '[data-testid="milestone-review-type-inline-picker"]';
    const EDIT_TRIGGER = '[data-milestone-inline-edit-trigger]';

    const picker =
      matrixWrapperRef.current?.querySelector<HTMLElement>(PICKER_SELECTOR) ??
      document.querySelector<HTMLElement>(PICKER_SELECTOR);

    const isInsideOpenPicker = (e: Event) => {
      if (!picker) return false;
      const path =
        typeof e.composedPath === 'function' ? e.composedPath() : null;
      if (path?.includes(picker)) return true;
      const t = e.target;
      return t instanceof Node && picker.contains(t);
    };

    const isSameColumnPencilPointer = (e: Event) => {
      const t = e.target;
      const el =
        t instanceof Element ? t : t instanceof Node ? t.parentElement : null;
      const trigger = el?.closest?.(EDIT_TRIGGER) as HTMLElement | null;
      if (!trigger || !matrixWrapperRef.current?.contains(trigger)) {
        return false;
      }
      const headerCell = trigger.closest<HTMLElement>(
        '[data-testid^="milestone-header-cell-"]'
      );
      const testId = headerCell?.getAttribute('data-testid') ?? '';
      const m = testId.match(MILESTONE_HEADER_CELL_TEST_ID_RE);
      const headerIdx = m ? parseInt(m[1], 10) : NaN;
      return headerIdx === reviewTypePickerColumnIndex;
    };

    const onDownCapture = (e: Event) => {
      if (isInsideOpenPicker(e) || isSameColumnPencilPointer(e)) return;
      dismissReviewTypePicker();
    };

    const opts: AddEventListenerOptions = { capture: true };
    if (typeof PointerEvent !== 'undefined') {
      window.addEventListener('pointerdown', onDownCapture, opts);
      return () =>
        window.removeEventListener('pointerdown', onDownCapture, opts);
    }
    window.addEventListener('mousedown', onDownCapture, opts);
    window.addEventListener('touchstart', onDownCapture, opts);
    return () => {
      window.removeEventListener('mousedown', onDownCapture, opts);
      window.removeEventListener('touchstart', onDownCapture, opts);
    };
  }, [reviewTypePickerColumnIndex, dismissReviewTypePicker]);

  /**
   * Pencil: if milestone is already Meeting / Async (or no-approval menu equivalent),
   * open the edit modal directly. Otherwise show the inline type-of-review menu first
   * (TBD / N/A apply inline; Meeting / Async from that menu still opens the modal).
   */
  const handleEditMilestone = (milestone: Milestone, index: number) => {
    invalidateApprovalStatusCheck();
    if (reviewTypePickerColumnIndex === index) {
      dismissReviewTypePicker();
      return;
    }

    const latestMilestone = enhancedMilestones[index] || milestone;

    const resolvedReviewType =
      effectiveReviewType(latestMilestone) ||
      inferTypeOfReviewFromStatus(latestMilestone.status) ||
      '';

    const enhancedMilestone = {
      ...latestMilestone,
      typeOfReview: resolvedReviewType || latestMilestone.typeOfReview
    };

    const localRowIndex = resolveLocalMilestoneRowIndex(
      localMilestones,
      enhancedMilestone,
      index
    );

    setSelectedMilestone(enhancedMilestone);
    setSelectedMilestoneIndex(localRowIndex);
    originalMilestoneSnapshotRef.current = enhancedMilestone;

    const rt = resolvedReviewType.trim().toLowerCase();
    const openEditModalDirect =
      rt === 'meeting' ||
      rt === 'async' ||
      rt === 'no approval required meeting' ||
      rt === 'no approval required async';

    if (openEditModalDirect) {
      setReviewTypePickerColumnIndex(null);
      setIsEditModalOpen(true);

      // Refresh this moment's alerts/notifications right as the modal opens —
      // the highlighted-field alerts behind it are cached from the last fetch,
      // so without this a milestone that moved to Approved/Re-Approved (or any
      // other end state) since then would still show its old alert on open.
      onNotificationRefresh?.();

      // On open, for an Async milestone already sent for approval (and not yet
      // Approved/Re-Approved), resolve the real-time email-send status once —
      // right here at the click that opens the modal, not in an effect — so the
      // modal can react to approver action taken outside this session, or to a
      // send that never actually went out.
      const individualMilestoneId =
        (enhancedMilestone as any).id || milestoneId;
      const shouldCheckApprovalStatus =
        rt === 'async' &&
        !isApprovedOrReApprovedStatus(enhancedMilestone.status) &&
        enhancedMilestone.sendForApproval === true &&
        !!issueId &&
        !!individualMilestoneId &&
        !!enhancedMilestone.name;

      if (shouldCheckApprovalStatus) {
        const editRevision = approvalEditRevisionRef.current;
        fetchMilestoneApprovalStatus(
          issueId,
          individualMilestoneId,
          enhancedMilestone.name
        )
          .then(result => {
            if (editRevision !== approvalEditRevisionRef.current) return;
            switch (result?.emailSentStatus) {
              case 'APPROVED':
              case 'NOT_APPROVED':
              case 'RE_APPROVED':
                // Approver already acted — refresh the data behind the open
                // modal without closing it. The click-time onNotificationRefresh
                // above (line ~744) ran before this async check resolved, so it
                // only ever saw the stale pre-resolution state — this is the
                // refresh that actually learned about the status change, and
                // must also refresh notifications, or the bell/alerts panel
                // stays stuck showing the pre-resolution alert (e.g. still
                // "Pending") even though the milestone info in the modal just
                // updated to Approved/Not Approved/Re-Approved.
                pendingApprovalStatusRefreshRef.current = editRevision;
                onDataRefresh?.();
                onNotificationRefresh?.();
                break;
              case 'NOT_SENT':
                // The prior send never actually went out. The modal disables
                // Save and Send while `emailSent` is true (it locks the
                // approval flow at the Pending stage) — clear it so the
                // button re-enables and the user can retry.
                setSelectedMilestone(prev =>
                  prev ? { ...prev, emailSent: false } : prev
                );
                break;
              case 'PENDING':
              default:
                // Still sending — no action needed.
                break;
            }
          })
          .catch(() => {
            if (editRevision !== approvalEditRevisionRef.current) return;
            ToastQueue.negative('Status update failed', { timeout: 5000 });
          });
      }
    } else {
      setIsEditModalOpen(false);
      setReviewTypePickerColumnIndex(index);
    }

    setHoverPopup({
      isVisible: false,
      milestone: null,
      position: { x: 0, y: 0 },
      arrowDirection: 'down',
      horizontalAlign: 'left'
    });
  };

  const buildTbdOrNaInlineUpdate = (
    reviewType:
      | typeof MILESTONE_REVIEW_TYPE.TBD
      | typeof MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED
  ) => ({
    reviewType,
    typeOfReview: reviewType,
    status: '',
    date: '',
    meetingScheduledOn: '',
    meetingScheduledAt: '',
    meetingLink: '',
    secondaryDate: '',
    displayScheduleDateTime: '',
    isEdited: true
  });

  /**
   * Returns `false` to keep the Edit Milestone modal open (save failed, or the
   * Save-and-Send email didn't go out and the user needs to resend) and `true`
   * once it's safe to close. The modal awaits this before closing itself.
   */
  const handleSaveMilestone = async (updatedData: any): Promise<boolean> => {
    if (selectedMilestoneIndex >= 0 && selectedMilestone) {
      invalidateApprovalStatusCheck();
      const localRowIndex = resolveLocalMilestoneRowIndex(
        localMilestones,
        selectedMilestone,
        selectedMilestoneIndex
      );

      // Create optimistic milestone with all updates
      const mergedReviewType =
        updatedData.reviewType ??
        updatedData.typeOfReview ??
        effectiveReviewType(selectedMilestone);
      const milestoneUpdates = {
        ...updatedData,
        ...(isInactiveApprovalReviewType(mergedReviewType)
          ? getNewApprovalCycleState('')
          : {})
      };
      const optimisticMilestone: Milestone = {
        ...selectedMilestone,
        ...milestoneUpdates,
        reviewType: mergedReviewType,
        // Keep legacy field identical so props/API skew cannot desync cell vs hover
        typeOfReview: mergedReviewType,
        isEdited: true,
        updatedAt: new Date().toISOString(),
        // `updatedData` never carries `emailSent` (EditMilestoneModal strips it
        // before it reaches here — it's server-computed), so without this the
        // `...selectedMilestone` spread above would leak the PREVIOUS approval
        // round's stale `emailSent` into a brand new Sent cycle and wrongly lock
        // the modal at "Pending" before this PATCH has even resolved — see
        // resolveOptimisticEmailSent's doc comment for the full scenario.
        emailSent: resolveOptimisticEmailSent({
          previousStatus: selectedMilestone.status,
          nextStatus: updatedData?.status,
          nextReviewType: mergedReviewType,
          previousEmailSent: selectedMilestone.emailSent
        })
      };

      // STEP 1: Update local milestones immediately for instant UI
      setLocalMilestones(prevMilestones => {
        const newMilestones = [...prevMilestones];
        const idx = resolveLocalMilestoneRowIndex(
          prevMilestones,
          selectedMilestone,
          localRowIndex
        );
        if (idx >= 0 && idx < newMilestones.length) {
          newMilestones[idx] = optimisticMilestone;
        }
        return newMilestones;
      });

      // STEP 2: Force immediate re-render
      setLocalUpdateTrigger(prev => prev + 1);

      // STEP 3: Update selectedMilestone with optimistic data for modal consistency
      setSelectedMilestone(optimisticMilestone);

      // The modal stays open (with its Save/Save-and-Send button enabled) until
      // this PATCH settles — see the `false` returns below on every failure path
      // and the email-not-sent case.
      try {
        // Get authentication headers
        const getAuthHeaders = () => {
          const token = getCookie('app_access_token');
          return {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          };
        };

        // Get API base URL
        const API_BASE_URL =
          process.env.REACT_APP_API_V2_URL ||
          (process.env.NODE_ENV === 'development'
            ? 'http://localhost:8080/product-roadmap/api/v2'
            : '/product-roadmap/api/v2');

        // Construct the API endpoint: /api/v2/milestones/{issueId}/{milestoneId}
        // Use the milestone's individual ID, not the prop milestoneId
        const individualMilestoneId =
          (selectedMilestone as any).id || milestoneId;

        // Validate required parameters
        if (!individualMilestoneId) {
          throw new Error('Milestone ID is required for milestone updates');
        }

        const apiUrl = `${API_BASE_URL}/milestones/${encodeURIComponent(
          issueId
        )}/${encodeURIComponent(individualMilestoneId)}`;

        // Prepare the full milestone data payload
        // ✅ CRITICAL: Remove API response metadata before constructing payload
        const selectedMilestoneAny = selectedMilestone as any;
        const {
          typeOfReview, // UI-only field
          data: apiData, // API response metadata
          message: apiMessage, // API response metadata
          errors: apiErrors, // API response metadata
          extraFields, // API response metadata
          // Server-only approval-tracking field (backend Milestone.extraFields, flattened to the
          // top level of the API response by @JsonAnyGetter — NOT nested under `extraFields`
          // above, so it survives that destructure untouched otherwise). MUST NOT be echoed back
          // on a manual save: the backend's re-approval detection (MilestonesService
          // #maybeApplyReapprovalFields) treats ANY real, non-blank lastApprovalRequestId in the
          // PATCH body as proof this write came from the genuine email-reply flow, and promotes
          // status to Re-Approved. Round-tripping the previous cycle's real request id here (e.g.
          // Sent -> email -> Approved -> manually reopened to Sent -> manually re-Approved, with
          // no new email) falsely re-triggers that promotion. Only
          // MilestoneApprovalStatusSyncService (which never goes through this fetch call) may ever
          // set this field.
          lastApprovalRequestId,
          ...cleanMilestoneData
        } = selectedMilestoneAny;

        // Map date fields to correct backend field names based on status
        // This ensures dates go to the right fields (e.g., approvedOn, notApprovedOn, sentOn, etc.)
        const mappedDateFields = mapMilestoneDateFields({
          ...milestoneUpdates,
          status: updatedData.status || selectedMilestone.status
        });

        const fullMilestoneData = {
          ...cleanMilestoneData, // Start with the cleaned milestone data (no API metadata)
          ...milestoneUpdates, // Merge with the updated fields (includes meetingLink if changed)
          ...mappedDateFields, // Apply mapped date fields (e.g., approvedOn, notApprovedOn, etc.)
          // Ensure we preserve the milestone name and any system fields
          name: selectedMilestone.name,
          milestoneAttributes: selectedMilestone.milestoneAttributes,
          isEdited: true
        };
        // Inline resets also carry this UI-only draft flag; only the server computes it.
        delete fullMilestoneData.emailSent;

        // Send-for-Approval: attach the FULL milestone table as raw HTML so the
        // backend can render it in the approval email. Built from every milestone
        // in the moment (with the just-edited row reflecting its new values) via
        // the same generator as the "Copy milestones" button, so the emailed
        // table matches the UI exactly.
        if (milestoneUpdates.sendForApproval) {
          try {
            const milestonesForTemplate = localMilestones.map((m, idx) =>
              idx === localRowIndex ? optimisticMilestone : m
            );
            (fullMilestoneData as any).emailTemplate =
              await generateMilestoneEmailTemplate(
                milestonesForTemplate,
                momentName,
                selectedMilestone.name
              );
          } catch (error) {
            console.error(
              'Failed to generate email template for approval:',
              error
            );
            (fullMilestoneData as any).emailTemplate = '';
          }
        }

        // Make PATCH request to the real API
        const response = await fetch(apiUrl, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify(fullMilestoneData)
        });

        if (!response.ok) {
          const errorText = await response.text();

          // Handle authentication errors with centralized handlers
          if (response.status === 401) {
            handle401Error('Updating milestone');
            // ROLLBACK: Revert optimistic updates
            setLocalMilestones(prevMilestones => {
              const newMilestones = [...prevMilestones];
              const idx = resolveLocalMilestoneRowIndex(
                prevMilestones,
                selectedMilestone,
                localRowIndex
              );
              if (idx >= 0 && idx < newMilestones.length) {
                newMilestones[idx] = selectedMilestone;
              }
              return newMilestones;
            });
            setLocalUpdateTrigger(prev => prev + 1);
            setSelectedMilestone(selectedMilestone);
            if (onDataRefresh) {
              onDataRefresh();
            }
            return false;
          }

          if (response.status === 403) {
            handle403Error('Updating milestone');
            // ROLLBACK: Revert optimistic updates
            setLocalMilestones(prevMilestones => {
              const newMilestones = [...prevMilestones];
              const idx = resolveLocalMilestoneRowIndex(
                prevMilestones,
                selectedMilestone,
                localRowIndex
              );
              if (idx >= 0 && idx < newMilestones.length) {
                newMilestones[idx] = selectedMilestone;
              }
              return newMilestones;
            });
            setLocalUpdateTrigger(prev => prev + 1);
            setSelectedMilestone(selectedMilestone);
            if (onDataRefresh) {
              onDataRefresh();
            }
            return false;
          }

          // Parse error message from API response for other errors
          let errorMessage = 'Failed to update milestone';
          try {
            const errorData = JSON.parse(errorText);

            // Check for data.errors array (API validation errors format)
            if (
              errorData.data?.errors &&
              Array.isArray(errorData.data.errors) &&
              errorData.data.errors.length > 0
            ) {
              // Join all error messages from the errors array
              errorMessage = errorData.data.errors.join('; ');
            } else {
              // Fallback to message or error field
              errorMessage =
                errorData.message || errorData.error || errorMessage;
            }
          } catch (e) {
            // If not JSON, use the text as error message
            if (errorText && errorText.trim().length > 0) {
              errorMessage = errorText;
            }
          }

          // Show error toast to user
          ToastQueue.negative(errorMessage, { timeout: 5000 });

          // ROLLBACK: Revert the MATRIX CELL's optimistic update only — the save
          // genuinely failed, so the table shouldn't show unconfirmed data. Do NOT
          // also reset `selectedMilestone` back to the pre-edit snapshot: it's the
          // modal's `milestone` prop (see the "for modal consistency" optimistic
          // set above), and resetting it here changes that prop's reference,
          // re-triggering EditMilestoneModal's milestone-sync effect — which wipes
          // every field the user edited this session (reviewType, status, other
          // dates, etc.), not just the one flagged by the error (e.g. a weekend
          // due date), even though the modal stays open for them to fix and retry.
          setLocalMilestones(prevMilestones => {
            const newMilestones = [...prevMilestones];
            const idx = resolveLocalMilestoneRowIndex(
              prevMilestones,
              selectedMilestone,
              localRowIndex
            );
            if (idx >= 0 && idx < newMilestones.length) {
              newMilestones[idx] = selectedMilestone;
            }
            return newMilestones;
          });

          // Force re-render to show reverted state
          setLocalUpdateTrigger(prev => prev + 1);

          // Trigger data refresh to sync with backend
          if (onDataRefresh) {
            onDataRefresh();
          }

          return false;
        }

        // Safely parse JSON (some endpoints may return empty body or non-JSON)
        let result: any = {};
        try {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            result = await response.json();
          } else {
            const text = await response.text();
            if (text && text.trim().length > 0) {
              try {
                result = JSON.parse(text);
              } catch (e) {
                // non-JSON text, ignore
                result = {};
              }
            }
          }
        } catch (e) {
          // Ignore JSON parse errors; proceed with optimistic UI update
          result = {};
        }

        const serverFields = pickMilestoneFieldsFromPatchResponse(result);
        const mergedServerReview =
          (serverFields.reviewType as string | undefined) ??
          (serverFields.typeOfReview as string | undefined) ??
          effectiveReviewType(optimisticMilestone);

        // `emailSent` is server-computed and deliberately stripped from the flat
        // milestone fields (see stripPatchResponseEnvelopeFields / the envelope
        // keys below) so it never round-trips back on the next save. Its
        // authoritative post-send value lives only inside the nested moment
        // collection of THIS same response — recover it (matched by
        // milestoneId/milestoneName) so we can fold it back onto the confirmed
        // milestone. Without this, the approval-flow lock (isApprovalReadOnly →
        // Pending stage needs emailSent === true) never engages until a full
        // refetch, and for an MVP milestone on an EOL moment no such refetch runs
        // at all (both onDataRefresh paths below are gated out), so the sent form
        // would stay editable indefinitely.
        const emailSentFromResponse =
          findEmailSentInPatchResponse(serverFields);

        // Envelope keys (issueId/milestoneId/milestoneName/moment/notifications/emailSent)
        // stripped here, specifically at the merge into persisted state — NOT from
        // `serverFields` itself, which findEmailSentInPatchResponse above still needs intact.
        // Left in, they'd round-trip back to the server on the next save and get stripped
        // again there, surfacing as a "fields not applied" warning instead of never being
        // sent at all. `emailSent` is then re-applied explicitly below from the
        // recovered value — a boolean means the server confirmed it, undefined
        // means the response carried none (e.g. a non-approval edit), in which
        // case we keep the optimistic value resolveOptimisticEmailSent computed.
        const confirmedMilestone: Milestone = {
          ...optimisticMilestone,
          ...stripPatchResponseEnvelopeFields(serverFields),
          reviewType: mergedServerReview,
          typeOfReview: mergedServerReview,
          emailSent:
            typeof emailSentFromResponse === 'boolean'
              ? emailSentFromResponse
              : optimisticMilestone.emailSent,
          isEdited:
            typeof serverFields.isEdited === 'boolean'
              ? serverFields.isEdited
              : optimisticMilestone.isEdited,
          ...(isInactiveApprovalReviewType(mergedServerReview)
            ? getNewApprovalCycleState('')
            : {})
        };

        // Update local state with confirmed data (keeps optimistic update visible)
        setLocalMilestones(prevMilestones => {
          const newMilestones = [...prevMilestones];
          const idx = resolveLocalMilestoneRowIndex(
            prevMilestones,
            selectedMilestone,
            localRowIndex
          );
          if (idx >= 0 && idx < newMilestones.length) {
            newMilestones[idx] = confirmedMilestone;
          }
          return newMilestones;
        });
        setLocalUpdateTrigger(prev => prev + 1);

        // Save-and-Send "sent" the request, but the backend couldn't actually
        // deliver the approval email (sent_pending was retired in favor of this
        // flag combo). Keep the modal open — with Save and Send enabled — so the
        // user can retry, instead of silently closing on a half-succeeded save.
        // `emailSentFromResponse` was recovered above (it isn't on the flat
        // single-milestone fields, only inside the nested moment collection).
        const wasSendForApprovalAttempt = isAsyncSentSendForApprovalAttempt({
          status: confirmedMilestone.status,
          reviewType: confirmedMilestone.reviewType,
          sendForApproval: confirmedMilestone.sendForApproval
        });

        const emailFailedToSend =
          wasSendForApprovalAttempt && emailSentFromResponse === false;

        if (emailFailedToSend) {
          setSelectedMilestone({
            ...confirmedMilestone,
            emailSent: emailSentFromResponse ?? false
          });
          ToastQueue.negative(MILESTONE_EMAIL_NOT_SENT_ERROR_MESSAGE, {
            timeout: 5000
          });
          return false;
        }

        if (wasSendForApprovalAttempt && emailSentFromResponse === true) {
          ToastQueue.positive(MILESTONE_EMAIL_SENT_SUCCESS_MESSAGE, {
            timeout: 5000
          });
        }

        // ✅ MARK SUCCESSFUL UPDATE to prevent prop sync from overwriting
        successfulUpdateRef.current = {
          index: localRowIndex,
          timestamp: Date.now()
        };

        // DISABLED: onMilestoneUpdate was causing flicker (optimistic -> revert -> correct)
        // Copy will require refresh until we find a solution that doesn't trigger parent re-renders
        // if (onMilestoneUpdate) {
        //   onMilestoneUpdate(selectedMilestoneIndex, confirmedMilestone);
        // }

        // ✅ Call notification API AFTER successful PATCH
        if (onNotificationRefresh) {
          onNotificationRefresh(result);
        }

        // Refresh the Home icon's pending-approvals indicator after any
        // successful milestone status save - covers Send for Approval,
        // Approve, and Reject alike (all three are just different `status`
        // values through this same success branch, see
        // isAsyncSentSendForApprovalAttempt/wasSendForApprovalAttempt above)
        // - not a separate success path per action. Backend response is
        // still the source of truth (see pendingApprovalsSlice.ts); this
        // just prompts a fresh read of it.
        dispatch(pendingApprovalsActions.loadPendingApprovalsCount());

        // 🎯 MVP MILESTONE → OVERVIEW MVP STATUS SYNC
        // When MVP milestone status OR reviewType is updated, also update the Overview MVP Status field
        // ✅ NEW: Also sync when reviewType changes to TBD or N/A (even if status doesn't change)
        // Diff against the true pre-edit snapshot, not `selectedMilestone` — the inline
        // review-type picker (handleInlineReviewTypeSelect) can pre-mutate
        // `selectedMilestone.status`/`.reviewType` before the modal even opens, which would
        // otherwise make this diff always come back "unchanged" for a brand-new milestone.
        const baselineMilestone =
          originalMilestoneSnapshotRef.current || selectedMilestone;
        const mvpStatusChanged =
          updatedData.status && updatedData.status !== baselineMilestone.status;
        const mvpReviewTypeChanged =
          updatedData.reviewType &&
          updatedData.reviewType !== baselineMilestone.reviewType;

        // Overview only renders an MVP Status field for moment types whose form
        // template defines it (see overviewFieldConfig.ts / priceChangesAndOffers.ts
        // "No MVP Status ... in Overview"); EOL and PCO-style offer moments never do.
        // Skip the sync entirely for those — nothing in Overview would ever surface
        // this PATCH, so it's just an unnecessary write.
        const momentTypeSupportsMvpStatus =
          !isEolMoment(momentType) &&
          !isPriceChangeStyleOfferMoment(momentType, pco);

        if (
          selectedMilestone.name?.toUpperCase() === 'MVP' &&
          (mvpStatusChanged || mvpReviewTypeChanged) &&
          momentTypeSupportsMvpStatus
        ) {
          try {
            // Get the NEW status label for display (what user sees in dropdown)
            // ✅ Priority: status > reviewType (for TBD/N/A cases)
            let newStatusLabel = '';

            // First check if status changed (normal case)
            if (updatedData.status) {
              const newStatusKey =
                typeof updatedData.status === 'string'
                  ? updatedData.status.toUpperCase()
                  : '';
              const newStatusConfig = newStatusKey
                ? MILESTONE_STATUSES[newStatusKey]
                : null;
              newStatusLabel =
                newStatusConfig?.label || updatedData.status || '';
            }
            // If no status, check reviewType (for TBD/N/A/Milestone Not Required)
            else if (updatedData.reviewType) {
              newStatusLabel = updatedData.reviewType;
            }

            // Get the OLD status label for display (convert backend key to display label)
            // ✅ Priority: status > reviewType (for TBD/N/A cases)
            let oldStatusLabel = '';

            if (selectedMilestone.status) {
              const oldStatusKey = selectedMilestone.status
                ? selectedMilestone.status.toUpperCase()
                : '';
              const oldStatusConfig = oldStatusKey
                ? MILESTONE_STATUSES[oldStatusKey]
                : null;
              oldStatusLabel =
                oldStatusConfig?.label || selectedMilestone.status || '';
            }
            // If no status, check reviewType (for TBD/N/A/Milestone Not Required)
            else if (selectedMilestone.reviewType) {
              oldStatusLabel = selectedMilestone.reviewType;
            }

            // Debug: Log what we're getting
            console.log('🔍 MVP Status Sync Debug:', {
              updatedStatus: updatedData.status,
              updatedReviewType: updatedData.reviewType,
              newStatusLabel: newStatusLabel,
              oldStatus: selectedMilestone.status,
              oldReviewType: selectedMilestone.reviewType,
              oldStatusLabel: oldStatusLabel,
              syncReason: mvpStatusChanged
                ? 'status changed'
                : 'reviewType changed'
            });

            // ✅ Capitalize first letter of both status labels (e.g., "approved" → "Approved")
            // Only capitalize if we have a valid string and it's not already showing underscores
            if (newStatusLabel && !newStatusLabel.includes('_')) {
              newStatusLabel =
                newStatusLabel.charAt(0).toUpperCase() +
                newStatusLabel.slice(1);
            }
            if (oldStatusLabel && !oldStatusLabel.includes('_')) {
              oldStatusLabel =
                oldStatusLabel.charAt(0).toUpperCase() +
                oldStatusLabel.slice(1);
            }

            // Update overview mvpStatus field with the new status label
            // Preserve existing content to avoid overwriting with empty (bug fix) — except
            // when the MVP milestone is newly marked "Milestone Not Required" (shown as N/A):
            // the Overview MVP Status text box is read-only (see Overview.tsx), so the user has
            // no way to fill it in themselves. Auto-fill it with "N/A" so rsd-info-incomplete
            // doesn't keep nagging for text the user can never type. Only applies when there's
            // no real content there yet — never clobbers genuine existing text.
            const hasExistingMvpContent =
              stripHtml(currentOverviewMvpStatusContent ?? '').trim().length >
              0;
            const isNewlyMarkedNotRequired =
              updatedData.reviewType ===
              MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED;
            const mvpStatusContent =
              isNewlyMarkedNotRequired && !hasExistingMvpContent
                ? 'N/A'
                : currentOverviewMvpStatusContent ?? '';
            const overviewPayload = {
              overview: {
                mvpStatus: {
                  option: newStatusLabel, // Use capitalized display label (e.g., "Approved", "Not Approved", "Scheduled")
                  content: mvpStatusContent
                }
              }
            };

            // Make PATCH request to update overview
            try {
              await updateMoment(issueId, overviewPayload);
            } catch (error) {
              ToastQueue.negative(
                'Failed to sync MVP status to Overview. Please try again.',
                { timeout: 5000 }
              );
              successfulUpdateRef.current = null;
              // The milestone PATCH itself still succeeded — don't block the
              // main save flow (and the modal close) over the overview sync.
              setIsEditModalOpen(false);
              setSelectedMilestone(null);
              setSelectedMilestoneIndex(-1);
              return true;
            }

            console.log(
              '✅ MVP milestone status synced to Overview:',
              newStatusLabel
            );

            // 🔄 TRIGGER FULL DATA REFRESH (including Overview section)
            // Dispatch a custom event that SlideOutPanel can listen to
            window.dispatchEvent(
              new CustomEvent('mvpStatusUpdated', {
                detail: { issueId, newStatus: newStatusLabel }
              })
            );

            // 🔔 SOFT-REFRESH NOTIFICATIONS after the overview sync above.
            // The earlier onNotificationRefresh(result) call (right after the milestone
            // PATCH) ran *before* this overview PATCH, so it can't reflect any
            // rsd-info-incomplete change caused by the new MVP status (e.g. the moment
            // just became complete/incomplete). Call again with no args — this forces a
            // real fetch (see refreshMomentNotifications) instead of reusing the
            // pre-sync embedded envelope — so the notification icon/badge count updates.
            if (onNotificationRefresh) {
              onNotificationRefresh();
            }

            // ✅ The 'mvpStatusUpdated' listener above already triggers a forced,
            // cache-bypassing refetch of the moment (see SlideOutPanel/index.tsx's
            // handleMvpStatusUpdate). Don't also call onDataRefresh(result) here —
            // `result` is the milestone-PATCH response captured *before* the overview
            // PATCH above ran, so any moment snapshot embedded in it still has the
            // pre-sync mvpStatus. Replaying it can land after the forced refetch and
            // clobber the fresh Overview data with stale content (race condition).
            setTimeout(() => {
              successfulUpdateRef.current = null;
            }, 2000);
          } catch (mvpSyncError) {
            console.error(
              '❌ Failed to sync MVP milestone status to Overview:',
              mvpSyncError
            );
            // Don't block the main milestone update flow if overview sync fails
            // Just log the error and continue
          }
        }

        // ✅ For non-MVP updates, trigger data refresh to fetch updated milestone data from moments API
        // This ensures parent component's apiData is updated with the latest milestone data
        if (selectedMilestone.name?.toUpperCase() !== 'MVP') {
          // Delay the refresh to allow optimistic update to be visible first
          setTimeout(() => {
            successfulUpdateRef.current = null;
            if (onDataRefresh) {
              onDataRefresh(result);
            }
          }, 300);
        }

        // TEMPORARILY DISABLED: Parent callbacks to prevent data reversion
        // These callbacks are causing the optimistic updates to be overridden

        // TODO: Re-enable these once we have a better sync mechanism
        // if (onMilestoneUpdate && result) {
        //   const confirmedMilestone = {
        //     ...selectedMilestone,
        //     ...updatedData,
        //     ...result, // Include any additional data from server response
        //     updatedAt: new Date().toISOString()
        //   };
        //   onMilestoneUpdate(selectedMilestoneIndex, confirmedMilestone);
        // }

        // ✅ Data refresh is triggered for all milestone updates to fetch fresh data from moments API
        // The successfulUpdateRef protection prevents flicker during the refresh

        // The PATCH succeeded (and, if applicable, the approval email actually
        // went out) — safe to close the modal now.
        setIsEditModalOpen(false);
        setSelectedMilestone(null);
        setSelectedMilestoneIndex(-1);

        return true;
      } catch (error) {
        // Handle network errors or unexpected failures
        // Extract error message
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Network error occurred while saving milestone';

        // Show error toast to user
        ToastQueue.negative(errorMessage, { timeout: 5000 });

        // ROLLBACK: revert only the matrix cell's optimistic update — see the
        // matching comment in the non-2xx branch above for why `selectedMilestone`
        // (the modal's `milestone` prop) must NOT also be reset here.
        setLocalMilestones(prevMilestones => {
          const newMilestones = [...prevMilestones];
          const idx = resolveLocalMilestoneRowIndex(
            prevMilestones,
            selectedMilestone,
            localRowIndex
          );
          if (idx >= 0 && idx < newMilestones.length) {
            newMilestones[idx] = selectedMilestone;
          }
          return newMilestones;
        });

        // Force re-render to show reverted state
        setLocalUpdateTrigger(prev => prev + 1);

        // Trigger data refresh to sync with backend
        if (onDataRefresh) {
          onDataRefresh();
        }

        return false;
      }
    } else {
      // If no milestone selected, just close the modal
      setIsEditModalOpen(false);
      setSelectedMilestone(null);
      setSelectedMilestoneIndex(-1);
      return true;
    }
  };

  const handleCloseModal = () => {
    invalidateApprovalStatusCheck();
    setIsEditModalOpen(false);
    setReviewTypePickerColumnIndex(null);
    setSelectedMilestone(null);
    setSelectedMilestoneIndex(-1);

    // Ensure hover popup is hidden when modal closes
    setHoverPopup({
      isVisible: false,
      milestone: null,
      position: { x: 0, y: 0 },
      arrowDirection: 'down',
      horizontalAlign: 'left'
    });
  };

  const handleInlineReviewTypeSelect = (choice: InlineReviewChoice) => {
    invalidateApprovalStatusCheck();
    setReviewTypePickerColumnIndex(null);

    if (
      choice === MILESTONE_REVIEW_TYPE.TBD ||
      choice === MILESTONE_REVIEW_TYPE.MILESTONE_NOT_REQUIRED
    ) {
      void handleSaveMilestone(buildTbdOrNaInlineUpdate(choice));
      return;
    }

    if (!selectedMilestone || selectedMilestoneIndex < 0) return;

    const currentDate = selectedMilestone.date || '';
    const shouldPreserveDate =
      currentDate &&
      currentDate.trim() !== '' &&
      currentDate.toUpperCase() !== 'N/A' &&
      currentDate.toUpperCase() !== 'TBD';

    const status = choice === 'Meeting' ? 'target' : 'async_target';

    setSelectedMilestone({
      ...selectedMilestone,
      ...(choice === MILESTONE_REVIEW_TYPE.ASYNC &&
      isInactiveApprovalReviewType(effectiveReviewType(selectedMilestone))
        ? getNewApprovalCycleState('')
        : {}),
      reviewType: choice,
      typeOfReview: choice,
      status,
      date: shouldPreserveDate ? currentDate : '',
      meetingScheduledOn: '',
      meetingScheduledAt: '',
      meetingLink: '',
      secondaryDate: ''
    });
    setIsEditModalOpen(true);
  };

  // Sort functionality is handled by parent component

  // Hover popup handlers with smart positioning (similar to TimelineCard date tooltip)
  const handleMouseEnter = (event: React.MouseEvent, milestone: Milestone) => {
    // Convert meetingScheduledAt time from stored format to user's local timezone
    // Use the SAME logic as cell display for consistency
    let convertedMilestone = milestone;

    // Priority 1: meetingScheduledOn is the live source the edit modal is built from;
    // displayScheduleDateTime is a cached copy that only refreshes on save and can go
    // stale (see ARM-830 E2E2 / Media Brainstorm — same issue as the cell fix above).
    const timestampToUse =
      milestone.meetingScheduledOn || milestone.displayScheduleDateTime;

    // Use the shared utility function for timezone conversion
    const converted = convertMilestoneTimestampToLocal(
      timestampToUse,
      milestone.meetingScheduledAt,
      true, // Include date in result
      {
        formatWithSpaces: true, // Format with spaces for hover popup
        use24HourFormat: true, // Use 24-hour format: "14:30" instead of "2:30 PM"
        includeTimezone: false // Don't include timezone (intuitive for users)
      }
    );

    if (converted && converted.convertedDate) {
      convertedMilestone = {
        ...milestone,
        meetingScheduledAt: converted.timeString,
        date: converted.convertedDate
      };
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 350; // Conservative estimate (actual max-width is 500px, but content usually fits in 350px)
    const popupHeight = 200; // Estimated popup height
    const margin = 16; // Minimum margin from panel edges
    const arrowOffset = 28; // Arrow position from left edge

    // Get the slideout panel boundaries
    const slideoutPanel = document.querySelector(
      '[data-testid="slideout-panel"]'
    ) as HTMLElement;

    if (!slideoutPanel) {
      // Fallback if slideout panel not found
      setHoverPopup({
        isVisible: true,
        milestone: convertedMilestone,
        position: { x: rect.left - 20, y: rect.bottom + 8 },
        cellPosition: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height
        },
        arrowDirection: 'down',
        horizontalAlign: 'left'
      });
      return;
    }

    const panelRect = slideoutPanel.getBoundingClientRect();

    // Calculate vertical position (above or below)
    let arrowDirection: 'up' | 'down' = 'down';
    let popupY = rect.bottom + 8; // Default: below cell

    // Check if popup would go off bottom of panel
    const spaceBelow = panelRect.bottom - rect.bottom;
    const spaceAbove = rect.top - panelRect.top;

    if (spaceBelow < popupHeight + margin && spaceAbove > spaceBelow) {
      // Position above if more space above
      arrowDirection = 'up';
      popupY = rect.top - popupHeight - 8;
    }

    // Calculate horizontal position (left, center, or right alignment)
    let popupX = rect.left - arrowOffset; // Default: align arrow with cell left edge
    let horizontalAlign: 'left' | 'center' | 'right' = 'left';

    // Check horizontal space constraints
    const minX = panelRect.left + margin;
    const maxX = panelRect.right - margin;

    // Calculate where the popup would appear with each alignment
    const leftAlignedX = rect.left - arrowOffset;
    const centerAlignedX = rect.left + rect.width / 2 - popupWidth / 2;
    const rightAlignedX = rect.right - popupWidth + arrowOffset;

    // Check if each alignment would fit within bounds
    const leftFits = leftAlignedX >= minX && leftAlignedX + popupWidth <= maxX;
    const centerFits =
      centerAlignedX >= minX && centerAlignedX + popupWidth <= maxX;
    const rightFits =
      rightAlignedX >= minX && rightAlignedX + popupWidth <= maxX;

    // Calculate distance from cell to panel edges
    const distanceFromLeft = rect.left - panelRect.left;
    const distanceFromRight = panelRect.right - rect.right;

    // Choose alignment based on what fits and which edge is closer
    if (leftFits && distanceFromLeft >= distanceFromRight) {
      // Left alignment fits and cell is closer to left - use left
      popupX = leftAlignedX;
      horizontalAlign = 'left';
    } else if (rightFits && distanceFromRight < distanceFromLeft) {
      // Right alignment fits and cell is closer to right - use right
      popupX = rightAlignedX;
      horizontalAlign = 'right';
    } else if (centerFits) {
      // Center fits - use center
      popupX = centerAlignedX;
      horizontalAlign = 'center';
    } else if (leftFits) {
      // Only left fits - use left
      popupX = leftAlignedX;
      horizontalAlign = 'left';
    } else if (rightFits) {
      // Only right fits - use right
      popupX = rightAlignedX;
      horizontalAlign = 'right';
    } else {
      // Nothing fits perfectly - choose based on which side has more space
      if (distanceFromRight > distanceFromLeft) {
        // More space on right - align left and clamp
        popupX = Math.max(minX, leftAlignedX);
        horizontalAlign = 'left';
      } else {
        // More space on left - align right and clamp
        popupX = Math.max(minX, Math.min(maxX - popupWidth, rightAlignedX));
        horizontalAlign = 'right';
      }
    }

    // Ensure popup stays within slideout panel bounds vertically
    const minY = panelRect.top + margin;
    const maxY = panelRect.bottom - popupHeight - margin;

    // If popup would go below panel, position it above the cell
    if (popupY + popupHeight > panelRect.bottom - margin) {
      popupY = rect.top - popupHeight - 8; // 8px gap above cell
      arrowDirection = 'up';

      // If it still doesn't fit above, position it within panel bounds
      if (popupY < minY) {
        popupY = minY;
      }
    }

    setHoverPopup({
      isVisible: true,
      milestone: convertedMilestone,
      position: { x: popupX, y: popupY },
      cellPosition: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      },
      arrowDirection,
      horizontalAlign
    });
  };

  const handleMouseLeave = () => {
    // Hide popup immediately when mouse leaves the cell
    setHoverPopup({
      isVisible: false,
      milestone: null,
      position: { x: 0, y: 0 },
      arrowDirection: 'down',
      horizontalAlign: 'left'
    });
  };

  // Hide popup when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (hoverPopup.isVisible) {
        setHoverPopup({
          isVisible: false,
          milestone: null,
          position: { x: 0, y: 0 },
          arrowDirection: 'down',
          horizontalAlign: 'left'
        });
      }
    };
    const handleWheel = () => {
      // Hide immediately on wheel to avoid "dragging" while scroll is in progress
      handleScroll();
    };
    const handleTouchMove = () => {
      handleScroll();
    };

    // Add scroll listeners to both the milestone container and the slideout panel
    const milestoneContainer = document.querySelector(
      '[data-testid="milestones-table-container"]'
    );
    const slideoutPanel = document.querySelector(
      '[data-testid="sliding-panel-modal"]'
    );

    if (milestoneContainer) {
      milestoneContainer.addEventListener('scroll', handleScroll, {
        passive: true
      });
      milestoneContainer.addEventListener('wheel', handleWheel, {
        passive: true
      });
      milestoneContainer.addEventListener('touchmove', handleTouchMove, {
        passive: true
      });
    }
    if (slideoutPanel) {
      slideoutPanel.addEventListener('scroll', handleScroll, { passive: true });
      slideoutPanel.addEventListener('wheel', handleWheel, { passive: true });
      slideoutPanel.addEventListener('touchmove', handleTouchMove, {
        passive: true
      });
    }

    // Also listen to window scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      if (milestoneContainer) {
        milestoneContainer.removeEventListener('scroll', handleScroll);
        milestoneContainer.removeEventListener('wheel', handleWheel);
        milestoneContainer.removeEventListener('touchmove', handleTouchMove);
      }
      if (slideoutPanel) {
        slideoutPanel.removeEventListener('scroll', handleScroll);
        slideoutPanel.removeEventListener('wheel', handleWheel);
        slideoutPanel.removeEventListener('touchmove', handleTouchMove);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [hoverPopup.isVisible]);

  // Hide popup when edit modal or inline review picker is open
  useEffect(() => {
    if (
      (isEditModalOpen || reviewTypePickerColumnIndex !== null) &&
      hoverPopup.isVisible
    ) {
      setHoverPopup({
        isVisible: false,
        milestone: null,
        position: { x: 0, y: 0 },
        arrowDirection: 'down',
        horizontalAlign: 'left'
      });
    }
  }, [isEditModalOpen, reviewTypePickerColumnIndex, hoverPopup.isVisible]);

  // Helper function to format milestone display text with proper types
  const formatMilestoneText = (milestone: Milestone) => {
    // Safely convert status to uppercase - handle null, undefined, or non-string values
    let statusKey = '';
    if (milestone.status && typeof milestone.status === 'string') {
      statusKey = milestone.status.toUpperCase();
    }

    // Special handling: If reviewType is "Not Required" or "Milestone Not Required", treat it as NOT_REQUIRED
    // Check both possible property names and values
    const reviewType = effectiveReviewType(milestone);
    if (
      reviewType === 'Not Required' ||
      reviewType === 'Milestone Not Required'
    ) {
      statusKey = 'NOT_REQUIRED';
    }

    const statusConfig = MILESTONE_STATUSES[statusKey];
    if (!statusConfig) {
      return {
        mainText: milestone.status || 'TBD',
        secondaryText: '',
        rolloverText: milestone.status || 'TBD'
      };
    }

    // Use meetingScheduledAt as formatted time range (new format: "11:11 AM - 12:12 PM UTC")
    let timeString = '';

    // Helper function to check if time string is valid (filter out NaN times)
    const isValidTimeString = (timeStr: string | undefined) => {
      if (!timeStr || timeStr.trim() === '') return false;
      // Filter out invalid time strings containing NaN
      if (timeStr.includes('NaN')) return false;
      return true;
    };

    // Priority 1: Calculate time from UTC timestamp for cross-timezone compatibility
    // This ensures all users see the time in their local timezone
    // Use displayScheduleDateTime if available (most accurate), fallback to meetingScheduledOn
    const timestampToUse =
      milestone.displayScheduleDateTime || milestone.meetingScheduledOn;

    // Use the shared utility function for timezone conversion
    const converted = convertMilestoneTimestampToLocal(
      timestampToUse,
      milestone.meetingScheduledAt,
      false, // Don't include date (handled separately below)
      {
        defaultDurationMinutes: 90, // Default 1.5 hours for formatMilestoneText
        formatWithSpaces: false, // Format without spaces
        use24HourFormat: true, // Use 24-hour format: "14:30" instead of "2:30 PM"
        includeTimezone: false // Don't include timezone (intuitive for users)
      }
    );

    if (converted) {
      timeString = converted.timeString;
    }
    // Priority 2: Fallback to stored time string (for backward compatibility or quarter/month dates)
    else if (isValidTimeString(milestone.meetingScheduledAt)) {
      timeString = milestone.meetingScheduledAt!;
    }

    // Normalize timezone notation (replace GMT+5:30 with IST, etc.)
    const normalizedTimeString = normalizeTimezoneInString(timeString);

    // For meeting_scheduled status, extract the date from meetingScheduledOn or displayScheduleDateTime
    // These contain UTC ISO timestamp like "2025-10-17T21:30:00Z"
    // We need to convert it to user's local timezone first, then extract the date
    // meetingScheduledOn checked first: it's the live source the edit modal is built from,
    // whereas displayScheduleDateTime is a cached copy that only gets refreshed on save and
    // can go stale if meetingScheduledOn is updated some other way (see ARM-830 E2E2 /
    // Media Brainstorm — cell showed a stale displayScheduleDateTime until the milestone
    // was re-saved).
    let displayDate = milestone.date;
    if (statusKey === 'MEETING_SCHEDULED') {
      const dateTimestamp =
        milestone.meetingScheduledOn || milestone.displayScheduleDateTime;
      if (dateTimestamp && /^\d{4}-\d{2}-\d{2}T/.test(dateTimestamp)) {
        try {
          // Convert UTC timestamp to user's local date
          const utcDate = new Date(dateTimestamp);
          if (!isNaN(utcDate.getTime())) {
            // Format in user's local timezone
            const year = utcDate.getFullYear();
            const month = String(utcDate.getMonth() + 1).padStart(2, '0');
            const day = String(utcDate.getDate()).padStart(2, '0');
            displayDate = `${year}-${month}-${day}`;
          }
        } catch (e) {
          // Fallback to simple extraction if conversion fails
          const isoMatch = dateTimestamp.match(/^(\d{4}-\d{2}-\d{2})/);
          if (isoMatch) {
            displayDate = isoMatch[1];
          }
        }
      }
    }

    // Get the formatted CELL display text (minimal) - Columns D-J
    const cellDisplayText = formatMilestoneDisplayText(statusKey, {
      date: displayDate,
      meetingScheduledAt: normalizedTimeString,
      secondaryDate: milestone.secondaryDate,
      approvalCondition:
        getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired
          ? 'Approval Required'
          : 'NO Approval Required',
      reviewType: milestone.reviewType || milestone.typeOfReview || 'Meeting'
    });

    // Get the formatted POPUP display text (detailed) - Columns after J
    const popupDisplayText = formatMilestonePopupText(
      statusKey,
      getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired ??
        false, // Use milestone attributes instead of milestone.required
      milestone.reviewType || milestone.typeOfReview, // Use reviewType as primary field
      displayDate, // date: string - use extracted date for meeting_scheduled, otherwise use milestone.date
      normalizedTimeString, // time: string (with user-friendly timezone)
      milestone.secondaryDate // secondaryDate: string
    );

    // Determine main text - respect explicit empty cellDisplayText
    // Normalize timezone in the final display text
    let mainText = '';

    // For NOT_REQUIRED status, always return empty string
    if (statusKey === 'NOT_REQUIRED') {
      mainText = '';
    } else if (cellDisplayText) {
      mainText = normalizeTimezoneInString(cellDisplayText);
    } else if (statusConfig.cellDisplayText !== undefined) {
      // If cellDisplayText is explicitly set (even to empty string), use it
      mainText = normalizeTimezoneInString(statusConfig.cellDisplayText);
    } else {
      // Fallback to label only if cellDisplayText is not defined
      mainText = statusConfig.label || '';
    }

    return {
      mainText,
      secondaryText: '', // Keep secondary text empty for minimal cell display
      rolloverText: normalizeTimezoneInString(
        popupDisplayText || statusConfig.rolloverCopy
      )
    };
  };

  // Helper function to get enhanced cell styling
  const getEnhancedCellStyling = (status?: string, milestone?: Milestone) => {
    // Safely convert status to uppercase - handle null, undefined, or non-string values
    let statusKey = '';
    if (status && typeof status === 'string') {
      statusKey = status.toUpperCase();
    }

    // Special handling: If reviewType is "Not Required" or "Milestone Not Required", treat it as NOT_REQUIRED

    // Check both possible property names and values
    const reviewType = milestone ? effectiveReviewType(milestone) : undefined;
    if (
      reviewType === 'Not Required' ||
      reviewType === 'Milestone Not Required'
    ) {
      statusKey = 'NOT_REQUIRED';
    }

    const statusConfig = MILESTONE_STATUSES[statusKey];
    const baseStyling = getCellStyling(statusKey);

    // Get the correct icon using our enhanced logic that handles null overrides
    const enhancedIcon = getEnhancedMilestoneIcon(statusKey, {
      approvalCondition:
        getMilestoneApprovalRequired(milestone?.name) ??
        milestone?.milestoneAttributes?.approvalRequired
          ? 'Approval Required'
          : 'NO Approval Required',
      reviewType: reviewType || 'Meeting' // Default to Meeting if not specified
    });

    return {
      // Return the complete styling object including striped patterns
      ...baseStyling,
      textColor: statusConfig?.textColor || baseStyling.color,
      iconName:
        enhancedIcon && MILESTONE_ICONS[enhancedIcon]
          ? MILESTONE_ICONS[enhancedIcon]
          : null,
      isStriped: statusConfig?.striped || false
    };
  };

  // Scroll functionality - memoized to prevent infinite re-renders
  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    // Hide chevrons if the entire table fits within the container (no scrolling needed)
    // This applies to both full screen and normal modes
    if (maxScrollLeft <= 0) {
      setShowLeftChevron(false);
      setShowRightChevron(false);
      return;
    }

    // Show chevrons based on scroll position when scrolling is needed
    const newShowLeftChevron = scrollLeft > 0;
    const newShowRightChevron = scrollLeft < maxScrollLeft - 1; // -1 for rounding issues

    // Only update state if values actually changed
    setShowLeftChevron(prev =>
      prev !== newShowLeftChevron ? newShowLeftChevron : prev
    );
    setShowRightChevron(prev =>
      prev !== newShowRightChevron ? newShowRightChevron : prev
    );
  }, [isFullScreen]);

  // Debounced version using requestAnimationFrame for better scroll performance
  const debouncedCheckScrollPosition = useCallback(() => {
    // Cancel any pending RAF
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    // Schedule new RAF
    rafRef.current = requestAnimationFrame(() => {
      checkScrollPosition();
      rafRef.current = null;
    });
  }, [checkScrollPosition]);

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    container.scrollTo({
      left: 0,
      behavior: 'smooth'
    });
  };

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    container.scrollTo({
      left: container.scrollWidth - container.clientWidth,
      behavior: 'smooth'
    });
  };

  // Chevron button event handlers to prevent interference with milestone hover/edit
  const handleChevronMouseEnter = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Hide any existing hover popup when hovering over chevron
    setHoverPopup(prev => ({ ...prev, isVisible: false }));
  };

  const handleChevronClick = (
    e: React.MouseEvent,
    direction: 'left' | 'right'
  ) => {
    e.preventDefault();
    e.stopPropagation();
    // Hide hover popup during scroll
    setHoverPopup(prev => ({ ...prev, isVisible: false }));

    if (direction === 'left') {
      scrollLeft();
    } else {
      scrollRight();
    }
  };

  // Check scroll position on mount and set up event listeners
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Initial check (not debounced)
      checkScrollPosition();

      // Set up event listeners with debounced handler
      container.addEventListener('scroll', debouncedCheckScrollPosition);
      window.addEventListener('resize', debouncedCheckScrollPosition);
      window.addEventListener('scroll', debouncedCheckScrollPosition);

      // Set up ResizeObserver to detect when table layout changes
      const resizeObserver = new ResizeObserver(() => {
        debouncedCheckScrollPosition();
      });
      resizeObserver.observe(container);

      return () => {
        // Cleanup event listeners
        container.removeEventListener('scroll', debouncedCheckScrollPosition);
        window.removeEventListener('resize', debouncedCheckScrollPosition);
        window.removeEventListener('scroll', debouncedCheckScrollPosition);
        resizeObserver.disconnect();

        // Cancel any pending RAF on unmount
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [checkScrollPosition, debouncedCheckScrollPosition]);

  // Separate effect to check scroll position when milestones change (but only if container exists)
  useEffect(() => {
    if (scrollContainerRef.current && enhancedMilestones.length > 0) {
      // Restore scroll position once after a key-forced remount
      if (!hasRestoredScrollRef.current && savedMilestoneScrollLeft > 0) {
        hasRestoredScrollRef.current = true;
        scrollContainerRef.current.scrollLeft = savedMilestoneScrollLeft;
      }

      // Use multiple timing strategies to ensure we catch the table after it's fully rendered
      const timeoutId1 = setTimeout(() => {
        checkScrollPosition();
      }, 0);

      const timeoutId2 = setTimeout(() => {
        checkScrollPosition();
      }, 100);

      const timeoutId3 = setTimeout(() => {
        checkScrollPosition();
      }, 500);

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
        clearTimeout(timeoutId3);
      };
    }
  }, [enhancedMilestones, checkScrollPosition]);

  // Force a final check after component is fully rendered and visible
  useEffect(() => {
    // This runs after all other effects and the DOM is fully updated
    const finalCheck = () => {
      if (scrollContainerRef.current) {
        checkScrollPosition();
      }
    };

    // Use requestAnimationFrame to ensure DOM is fully painted
    const rafId = requestAnimationFrame(finalCheck);

    return () => cancelAnimationFrame(rafId);
  }); // No dependency array - runs after every render

  // Don't show anything if no milestones - let the Add button show instead
  if (!milestones || milestones.length === 0) {
    return null;
  }

  return (
    <div
      ref={matrixWrapperRef}
      className={styles.milestoneMatrixWrapper}
      data-milestone-alert-scope={alertHighlightScope}
    >
      {/* Left Chevron Button */}
      {showLeftChevron &&
        !isEditModalOpen &&
        reviewTypePickerColumnIndex === null && (
          <button
            data-testid="milestone-scroll-left-button"
            className={`${styles.chevronButton} ${styles.leftChevron}`}
            onClick={e => handleChevronClick(e, 'left')}
            onMouseEnter={handleChevronMouseEnter}
            onMouseLeave={e => e.stopPropagation()}
            aria-label="Scroll left"
          >
            <img
              src="/icons/chevron-right.svg"
              alt="Scroll left"
              className={styles.chevronIcon}
              style={{ transform: 'rotate(180deg)' }}
            />
          </button>
        )}

      {/* Scrollable Table Container */}
      <div
        ref={scrollContainerRef}
        data-testid="milestones-table-container"
        className={`${styles.milestonesTableContainer} ${
          isFullScreen
            ? styles.fullScreenTable
            : isCommentPanelOpen
            ? styles.normalTableWithComments
            : styles.normalTable
        }`}
      >
        {/* Header Row - Restored original structure */}
        <div
          data-testid="milestones-header-row"
          className={styles.milestonesHeaderRow}
        >
          {enhancedMilestones.map((milestone, index) => {
            const widthClass = getCellWidthClass(milestone.name);
            const headerHighlightIssue = getDeclarativeMilestoneHighlightIssue(
              milestone,
              'header'
            );
            return (
              <div
                key={`header-${index}`}
                data-testid={`milestone-header-cell-${index}`}
                data-milestone-name-key={milestoneNameKeyAttr(milestone.name)}
                {...(phaseName
                  ? {
                      'data-milestone-phase-key':
                        milestonePhaseKeyAttr(phaseName)
                    }
                  : {})}
                {...(headerHighlightIssue
                  ? {
                      'data-alert-issue-id': issueId,
                      'data-alert-field-ref':
                        headerHighlightIssue.fieldRef ?? 'null',
                      'data-alert-rule-id': headerHighlightIssue.ruleId
                    }
                  : {})}
                className={`${styles.milestoneHeaderCell} ${
                  styles[widthClass]
                }${headerHighlightIssue ? ' alert-field-highlight' : ''}`}
              >
                <span
                  data-testid={`milestone-header-label-${index}`}
                  className={styles.milestoneHeaderLabel}
                >
                  {milestone.name}
                </span>
                <span
                  data-testid={`milestone-icon-approval-row-${index}`}
                  className={styles.iconApprovalRow}
                >
                  {/* Show 'A' tag based on milestone attributes approvalRequired */}
                  {(getMilestoneApprovalRequired(milestone?.name) ??
                    milestone?.milestoneAttributes?.approvalRequired) && (
                    <span
                      data-testid={`milestone-icon-approval-${index}`}
                      className={styles.iconApproval}
                    >
                      A
                    </span>
                  )}
                  {/* Show email icon based on milestone attributes async */}
                  {milestone.milestoneAttributes?.async && (
                    <a
                      href="https://wiki.corp.adobe.com/x/GKLAvg"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`milestone-email-link-${index}`}
                      title="Async Milestone Documentation"
                      className={styles.emailIconLink}
                    >
                      <img
                        data-testid={`milestone-icon-email-${index}`}
                        src="/icons/icon-email.svg"
                        alt="Async"
                        className={styles.iconEmail}
                      />
                    </a>
                  )}
                  {isEditable && canEditMilestones && (
                    <span
                      className={styles.pencilIcon}
                      data-milestone-inline-edit-trigger
                      onClick={e => {
                        e.stopPropagation();
                        const cell = (
                          e.currentTarget as HTMLElement
                        ).closest<HTMLElement>(
                          '[data-testid^="milestone-header-cell-"]'
                        );
                        const label = cell?.querySelector<HTMLElement>(
                          '[data-testid^="milestone-header-label-"]'
                        );
                        inlinePickerAnchorRef.current =
                          label ?? (e.currentTarget as HTMLElement);
                        handleEditMilestone(milestone, index);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img src="/icons/icon-edit.svg" alt="Edit" />
                    </span>
                  )}
                  {onOpenFeedback && (
                    <span className={styles.feedbackIconWrapper}>
                      <button
                        type="button"
                        data-testid={`milestone-feedback-button-${index}`}
                        className={styles.feedbackIconButton}
                        onClick={e => {
                          e.stopPropagation();
                          // Same fallback fetchMilestoneApprovalStatus's own
                          // call site above already relies on - an
                          // individual milestone entry doesn't reliably
                          // carry its own `id`, so fall back to this whole
                          // matrix's own `milestoneId` prop rather than
                          // requiring it unconditionally.
                          const individualMilestoneId =
                            (milestone as any).id || milestoneId;
                          if (!issueId || !individualMilestoneId) return;
                          onOpenFeedback({
                            issueId,
                            milestoneId: individualMilestoneId,
                            milestoneName: milestone.name
                          });
                        }}
                        aria-label={
                          milestone.hasUnreadFeedback
                            ? 'Unread feedback'
                            : 'Feedback'
                        }
                      >
                        <img
                          src={S2CommentTextIcon}
                          alt=""
                          width={18}
                          height={18}
                        />
                        {milestone.hasUnreadFeedback && (
                          <span className={styles.feedbackBadge} />
                        )}
                      </button>
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {/* Value Row - Restored original structure */}
        <div
          className={styles.milestonesValueRow}
          data-testid="milestones-value-row"
        >
          {enhancedMilestones.map((milestone, index) => {
            // Determine the correct status key
            // Special handling: If reviewType is "Not Required" or "Milestone Not Required", treat it as NOT_REQUIRED
            const reviewType = effectiveReviewType(milestone);
            // Safely convert status to uppercase - handle null, undefined, or non-string values
            let statusKey = '';
            if (milestone.status && typeof milestone.status === 'string') {
              statusKey = milestone.status.toUpperCase();
            }

            if (
              reviewType === 'Not Required' ||
              reviewType === 'Milestone Not Required'
            ) {
              statusKey = 'NOT_REQUIRED';
            } else if (!statusKey) {
              // Only default to TBD if not "Not Required"
              statusKey = 'TBD';
            }

            const cellStyling = getEnhancedCellStyling(statusKey, milestone);

            const displayText = formatMilestoneText(milestone);
            const widthClass = getCellWidthClass(milestone.name);
            const dateHighlightIssue = getDeclarativeMilestoneHighlightIssue(
              milestone,
              'date'
            );

            // Determine if we should show TBD or status-based content
            // Show TBD ONLY when reviewType is explicitly "TBD"
            // Do NOT show TBD for "Milestone Not Required" or when there's no status
            const showTBD =
              reviewType === 'TBD' ||
              (milestone.status === 'TBD' &&
                reviewType !== 'Milestone Not Required' &&
                reviewType !== 'Not Required');

            return (
              <div
                key={`value-${index}`}
                data-milestone-name={milestone.name}
                data-milestone-name-key={milestoneNameKeyAttr(milestone.name)}
                {...(phaseName
                  ? {
                      'data-milestone-phase-key':
                        milestonePhaseKeyAttr(phaseName)
                    }
                  : {})}
                data-milestone-field="date"
                {...(dateHighlightIssue
                  ? {
                      'data-alert-issue-id': issueId,
                      'data-alert-field-ref':
                        dateHighlightIssue.fieldRef ?? 'null',
                      'data-alert-rule-id': dateHighlightIssue.ruleId
                    }
                  : {})}
                className={`${styles.milestoneValueCell} ${
                  styles[widthClass]
                } ${cellStyling.isStriped ? styles.stripedCell : ''}${
                  dateHighlightIssue ? ' alert-field-highlight' : ''
                }`}
                style={{
                  // Use the complete styling from getCellStyling which includes striped patterns
                  ...(cellStyling.background
                    ? { background: cellStyling.background }
                    : { backgroundColor: cellStyling.backgroundColor }),
                  color: cellStyling.textColor || cellStyling.color,
                  pointerEvents: 'auto' // Ensure pointer events are enabled for hover to work from outside
                  // Removed borderColor to let CSS borders work properly
                }}
                onMouseEnter={e => handleMouseEnter(e, milestone)}
                onMouseLeave={handleMouseLeave}
                // Commented out: Click handler to open edit milestone modal
                // onClick={() => {
                //   setSelectedMilestone(milestone);
                //   setSelectedMilestoneIndex(index);
                //   setIsEditModalOpen(true);

                //   // Hide hover popup when opening edit modal
                //   setHoverPopup({
                //     isVisible: false,
                //     milestone: null,
                //     position: { x: 0, y: 0 },
                //     arrowDirection: 'down'
                //   });
                // }}
              >
                {showTBD ? (
                  <div className={styles.milestoneValueContent}>
                    {/* Always render icons container to maintain consistent text alignment */}
                    <div className={styles.iconsContainer}></div>
                    <span
                      className={styles.statusText}
                      style={{ color: '#EA3829' }}
                    >
                      TBD
                    </span>
                  </div>
                ) : (
                  <div className={styles.milestoneValueContent}>
                    {/* Always render icons container to maintain consistent text alignment */}
                    <div className={styles.iconsContainer}>
                      {/* Primary status icon - only render if icon exists */}
                      {cellStyling.iconName &&
                        MilestoneIcons[cellStyling.iconName] && (
                          <span
                            className={styles.statusIcon}
                            aria-label={milestone.status}
                          >
                            {(() => {
                              const IconComponent =
                                MilestoneIcons[cellStyling.iconName];
                              return <IconComponent size={12} />; // Match Figma Frame 314663 icon size
                            })()}
                          </span>
                        )}
                      {/* Recording/Video icon when recording link is present */}
                      {milestone.recordingLink && (
                        <a
                          href={normalizeMilestoneLink(milestone.recordingLink)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${styles.statusIcon} ${styles.clickableIcon} ${styles.recordingIcon}`}
                          aria-label="Open recording in new tab"
                          onClick={e => e.stopPropagation()}
                          style={{ cursor: 'pointer', textDecoration: 'none' }}
                        >
                          <MilestoneIcons.video size={12} />{' '}
                          {/* Match Figma Frame 314663 icon size */}
                        </a>
                      )}
                    </div>
                    {/* Make milestone text clickable if:
                        1. MVP milestone (always clickable to scroll to Overview)
                        2. OR has document link AND text is not N/A/TBD */}
                    {(() => {
                      const hasDisplayText = Boolean(
                        displayText.mainText?.trim()
                      );
                      const useUneditedTypography =
                        milestone.isEdited === false &&
                        hasDisplayText &&
                        !isMilestonePastDue(milestone);
                      const dateTextStyle = getMilestoneDateTextStyle(
                        milestone,
                        hasDisplayText
                      );

                      return milestone.name.toUpperCase() === 'MVP' ||
                        (milestone.documentLink &&
                          displayText.mainText &&
                          displayText.mainText.toUpperCase() !== 'N/A' &&
                          displayText.mainText.toUpperCase() !== 'TBD') ? (
                        <a
                          href={
                            milestone.name.toUpperCase() === 'MVP'
                              ? parentMomentId
                                ? normalizeMilestoneLink(
                                    `/moments/${encodeURIComponent(
                                      parentMomentId
                                    )}`
                                  )
                                : // No real href for the same-page scroll case: a genuine
                                  // `href="#mvp-section"` lets the browser's native anchor
                                  // navigation (modifier-click, or Safari's own hash-jump
                                  // default) fire independently of this onClick's
                                  // preventDefault, opening a duplicate tab instead of
                                  // scrolling in place. handlePrintSlideout adds a real
                                  // href back in, only for the print window, using the
                                  // data-mvp-link marker below.
                                  undefined
                              : normalizeMilestoneLink(
                                  milestone.documentLink || ''
                                )
                          }
                          {...(milestone.name.toUpperCase() === 'MVP' &&
                          !parentMomentId
                            ? { 'data-mvp-link': 'true' }
                            : {})}
                          className={`${styles.statusText} ${
                            useUneditedTypography ? styles.uneditedDateText : ''
                          } ${styles.clickableText}`}
                          onClick={e => {
                            e.stopPropagation(); // Prevent cell click events
                            e.preventDefault(); // Preserve the existing SPA/window.open behavior.

                            // Special handling for MVP milestone - scroll to MVP section in Overview
                            if (milestone.name.toUpperCase() === 'MVP') {
                              // If this is an inherited milestone (has parentMomentId), navigate to parent RSD
                              if (parentMomentId) {
                                // Store MVP navigation intent in sessionStorage to be picked up after navigation
                                sessionStorage.setItem(
                                  'scrollToMvpSection',
                                  'true'
                                );

                                // Navigate to parent moment RSD using React Router (same as chevron navigation)
                                const searchParams = new URLSearchParams(
                                  location.search
                                );
                                const momentUrl = `/moments/${encodeURIComponent(
                                  parentMomentId
                                )}`;
                                const fullUrl = searchParams.toString()
                                  ? `${momentUrl}?${searchParams.toString()}`
                                  : momentUrl;

                                navigate(fullUrl, { replace: false });
                              } else {
                                // Not inherited - scroll to MVP section in current RSD
                                // ✅ First, dispatch event to auto-expand Overview accordion
                                window.dispatchEvent(
                                  new CustomEvent('expandOverviewForMvp')
                                );

                                // ✅ Wait for accordion to actually expand in DOM
                                // Use multiple animation frames to ensure expansion is complete
                                requestAnimationFrame(() => {
                                  requestAnimationFrame(() => {
                                    setTimeout(() => {
                                      const mvpSection =
                                        document.getElementById('mvp-section');
                                      if (mvpSection) {
                                        mvpSection.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'start'
                                        });

                                        // Trigger highlight animation via custom event
                                        window.dispatchEvent(
                                          new CustomEvent('highlightMvpSection')
                                        );
                                      } else {
                                        console.warn(
                                          '⚠️ MVP section not found in DOM after expansion'
                                        );
                                      }
                                    }, 300); // Additional delay after RAF for accordion animation
                                  });
                                });
                              }
                            } else {
                              // For all other milestones, open document link in new tab
                              window.open(
                                milestone.documentLink,
                                '_blank',
                                'noopener,noreferrer'
                              );
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'none',
                            ...dateTextStyle
                          }}
                          title={
                            milestone.name.toUpperCase() === 'MVP'
                              ? parentMomentId
                                ? 'Click to view MVP section in parent RSD'
                                : 'Click to view MVP section in Overview'
                              : 'Click to open document'
                          }
                        >
                          {displayText.mainText}
                        </a>
                      ) : (
                        <span
                          className={`${styles.statusText} ${
                            useUneditedTypography ? styles.uneditedDateText : ''
                          }`}
                          style={dateTextStyle}
                        >
                          {displayText.mainText}
                        </span>
                      );
                    })()}
                    {milestone.meetingLink && (
                      <a
                        href={normalizeMilestoneLink(milestone.meetingLink)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-print-only-link
                        onClick={e => e.stopPropagation()}
                      >
                        Meeting
                      </a>
                    )}
                    {displayText.secondaryText && (
                      <span className={styles.secondaryText}>
                        {displayText.secondaryText}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {reviewTypePickerColumnIndex !== null && !isEditModalOpen && (
        <MilestoneReviewTypeInlinePicker
          key={reviewTypePickerColumnIndex}
          anchorRef={inlinePickerAnchorRef}
          overlayRootRef={matrixWrapperRef}
          selectedReviewType={effectiveReviewType(selectedMilestone) ?? ''}
          excludedReviewTypes={getMilestoneExcludedReviewTypes(
            selectedMilestone?.name
          )}
          onSelect={handleInlineReviewTypeSelect}
          onDismiss={dismissReviewTypePicker}
        />
      )}

      {/* Edit Modal */}
      <EditMilestoneModal
        open={isEditModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveMilestone}
        onApprovalCycleStart={invalidateApprovalStatusCheck}
        tier={tier}
        productGroups={productGroups}
        sendForApprovalContext={sendForApprovalContext}
        milestone={
          selectedMilestone
            ? (() => {
                const milestoneData = {
                  name: selectedMilestone.name,
                  reviewType: effectiveReviewType(selectedMilestone), // Keep modal in sync with legacy + primary fields
                  status: selectedMilestone.status,
                  date: selectedMilestone.date,
                  meetingScheduledOn: selectedMilestone.meetingScheduledOn,
                  meetingScheduledAt: selectedMilestone.meetingScheduledAt,
                  meetingLink: selectedMilestone.meetingLink,
                  secondaryDate: selectedMilestone.secondaryDate,
                  documentLink: selectedMilestone.documentLink,
                  recordingLink: selectedMilestone.recordingLink,
                  notesLink: selectedMilestone.notesLink,
                  approver: selectedMilestone.approver,
                  approverUid: selectedMilestone.approverUid,
                  // Historical/enhanced fields
                  originalApprovedOn: selectedMilestone.originalApprovedOn,
                  reApprovedOn: selectedMilestone.reApprovedOn,
                  approvedBy: selectedMilestone.approvedBy,
                  reApprovedBy: selectedMilestone.reApprovedBy,
                  notApprovedBy: selectedMilestone.notApprovedBy,
                  // Raw API fields — EditMilestoneModal uses these to suppress Approved/Completed autofill (UC5/UC6)
                  approvedOn: selectedMilestone.approvedOn,
                  completedOn: selectedMilestone.completedOn,
                  isEdited: selectedMilestone.isEdited,
                  // Milestone attributes for approval required display
                  milestoneAttributes: selectedMilestone.milestoneAttributes,
                  // Persisted Send-for-Approval data — prefills the flow + form on re-open
                  sendForApproval: selectedMilestone.sendForApproval,
                  cc: selectedMilestone.cc,
                  ccUid: selectedMilestone.ccUid,
                  subject: selectedMilestone.subject,
                  noteToApprover: selectedMilestone.noteToApprover,
                  emailSent: selectedMilestone.emailSent
                };

                return milestoneData;
              })()
            : undefined
        }
      />

      {/* Hover Popup */}
      <MilestoneHoverPopup
        milestone={
          hoverPopup.milestone || {
            name: '',
            required: false,
            reviewType: '',
            status: '',
            date: '',
            approver: '',
            milestoneAttributes: {
              async: false,
              audited: false,
              approvalRequired: false
            }
          }
        }
        isVisible={hoverPopup.isVisible}
        position={hoverPopup.position}
        arrowDirection={hoverPopup.arrowDirection}
        horizontalAlign={hoverPopup.horizontalAlign}
        onMouseEnter={() => {
          // Keep popup visible when hovering over it
        }}
        onMouseLeave={() => {
          // Hide popup when mouse leaves the popup
          setHoverPopup({
            isVisible: false,
            milestone: null,
            position: { x: 0, y: 0 },
            arrowDirection: 'down',
            horizontalAlign: 'left'
          });
        }}
      />

      {/* Right Chevron Button */}
      {showRightChevron &&
        !isEditModalOpen &&
        reviewTypePickerColumnIndex === null && (
          <button
            data-testid="milestone-scroll-right-button"
            className={`${styles.chevronButton} ${styles.rightChevron} ${
              isFullScreen ? styles.fullScreenChevron : styles.normalChevron
            }`}
            onClick={e => handleChevronClick(e, 'right')}
            onMouseEnter={handleChevronMouseEnter}
            onMouseLeave={e => e.stopPropagation()}
            aria-label="Scroll right"
          >
            <img
              src="/icons/chevron-right.svg"
              alt="Scroll right"
              className={styles.chevronIcon}
            />
          </button>
        )}
    </div>
  );
};

export default MilestoneMatrixFromAPI;
